import { Badge } from "@/components/ui/badge"
import { Bell, Bookmark, CalendarDays, ChevronsDownUp, ChevronsUpDown, ClipboardList, ListTodo, FileSpreadsheet, LayoutDashboard, LibraryBig, LogOut, MessageSquarePlus, Settings } from "lucide-react"
import { type CSSProperties, useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { version as appVersion } from "../../package.json"
import { BrandMark } from "@/components/brand-mark"
import { ChangelogDialog } from "@/components/changelog-dialog"
import { PageTopBarSlotContext } from "@/components/page-top-bar-context"
import farolLogo from "@/assets/farol-10.png"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useAuth } from "@/hooks/use-auth"
import { useMyTasksQuery } from "@/hooks/use-editorial-data"
import type { Role } from "@/types/domain"

const roleLabels: Record<Role, string> = { ADMIN: "Administrador", OPS: "Operador", VIEWER: "Leitor" }

const navigation = [
  { to: "/", label: "Visão geral", icon: LayoutDashboard, end: true },
  { to: "/minhas-tarefas", label: "Minhas tarefas", icon: ClipboardList },
  { to: "/catalogo", label: "Catálogo", icon: LibraryBig },
  { to: "/producao", label: "Produção", icon: ListTodo },
  { to: "/calendario", label: "Calendário", icon: CalendarDays },
  { to: "/selos", label: "Selos", icon: Bookmark, adminOnly: true },
  { to: "/relatorios", label: "Relatórios", icon: FileSpreadsheet, disabled: true, hidden: true },
  { to: "/notificacoes", label: "Notificações", icon: Bell, disabled: true, hidden: true },
]

const settingsNavigation = [
  { to: "/graficas", label: "Gráficas", adminOnly: false },
  { to: "/configuracoes/colecoes", label: "Coleções", adminOnly: true },
  { to: "/configuracoes/itens-dos-kits", label: "Tipos de itens", adminOnly: true },
  { to: "/configuracoes/modelos-de-kits", label: "Modelos de kits", adminOnly: true },
  { to: "/configuracoes/fluxos-de-trabalho", label: "Fluxos de trabalho", adminOnly: true, disabled: false },
  { to: "/configuracoes/estimativas", label: "Estimativa de tempo", adminOnly: true },
  { to: "/configuracoes/google-drive", label: "Google Drive", adminOnly: true },
  { to: "/configuracoes/usuarios", label: "Usuários", adminOnly: true },
]

const sidebarStorageKey = "barca-sidebar-collapsed-v1"
const calendarSearchStorageKey = "barca-calendar-search-v1"

function getPersistedCalendarSearch() {
  try {
    return window.sessionStorage.getItem(calendarSearchStorageKey) ?? ""
  } catch {
    return ""
  }
}

function persistCalendarSearch(search: string) {
  try {
    if (search) window.sessionStorage.setItem(calendarSearchStorageKey, search)
    else window.sessionStorage.removeItem(calendarSearchStorageKey)
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function isRouteActive(pathname: string, to: string, end = false) {
  return pathname === to || (!end && to !== "/" && pathname.startsWith(`${to}/`))
}

function AppSidebar() {
  const location = useLocation()
  const { user, isDemo, signOut } = useAuth()
  const { isMobile, open, setOpen, setOpenMobile } = useSidebar()
  const triggerLabel = open ? "Colapsar barra lateral" : "Expandir barra lateral"
  const inSettings = location.pathname.startsWith("/configuracoes") || location.pathname.startsWith("/graficas")
  const [settingsOpen, setSettingsOpen] = useState(inSettings)
  const calendarSearch = location.pathname === "/calendario" ? location.search : getPersistedCalendarSearch()
  const myTasks = useMyTasksQuery()
  const readyTaskCount = myTasks.data?.filter((task) => task.status === "READY").length ?? 0
  const feedbackUrl = new URL("https://forms.clickup.com/90171493163/f/2kza7jtb-4497/36CUXP6VSIQ7ASSLNE")
  feedbackUrl.searchParams.set("Página de origem", new URL(`${location.pathname}${location.search}${location.hash}`, window.location.origin).href)
  feedbackUrl.searchParams.set("Versão do FAROL", appVersion)
  if (user?.email) feedbackUrl.searchParams.set("Email de usuário", user.email)

  useEffect(() => {
    if (location.pathname === "/calendario") persistCalendarSearch(location.search)
  }, [location.pathname, location.search])

  function closeMobileNavigation() {
    if (isMobile) setOpenMobile(false)
  }

  function changeSettingsOpen(nextOpen: boolean) {
    if (!open) {
      setOpen(true)
      setSettingsOpen(true)
      return
    }

    setSettingsOpen(nextOpen)
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center rounded-none border-b border-sidebar-border px-4 py-0">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          <Link to="/" aria-label="Farol, ir para a visão geral" onClick={() => setOpenMobile(false)} className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring group-data-[collapsible=icon]:hidden">
            <BrandMark label="farol" labelClassName="text-xl" symbolImageSrc={farolLogo} symbolImageAlt="" symbolClassName="bg-black" />
          </Link>
          {isMobile ? (
            <SidebarTrigger aria-label="Fechar menu" className="active:scale-[0.96] transition-transform" />
          ) : (
            <Tooltip>
              <TooltipTrigger render={<SidebarTrigger aria-label={triggerLabel} className="active:scale-[0.96] transition-transform" />} />
              <TooltipContent side={open ? "bottom" : "right"}>{triggerLabel}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-thin">
        <SidebarGroup className="px-3 py-4 transition-[padding] duration-200 ease-linear group-data-[collapsible=icon]:px-4">
          <SidebarGroupContent>
            <nav aria-label="Navegação principal">
              <SidebarMenu className="gap-1">
                {navigation.filter((item) => !item.hidden && (!item.adminOnly || user?.role === "ADMIN")).map(({ to, label, icon: Icon, end, disabled }) => (
                  <SidebarMenuItem key={to}>
                    {disabled ? (
                      <Tooltip>
                        <TooltipTrigger render={<span className="block" />}>
                            <SidebarMenuButton disabled aria-disabled="true" className="h-9 px-3 font-sans font-medium">
                              <Icon />
                              <span>{label}</span>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                        <TooltipContent side="right" hidden={open || isMobile}>{label}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <SidebarMenuButton isActive={isRouteActive(location.pathname, to, end)} tooltip={label} className="h-9 px-3 font-sans font-medium" render={<NavLink
                          aria-label={label}
                          to={to === "/calendario" ? `/calendario${calendarSearch}` : to === "/catalogo" && location.pathname.startsWith("/catalogo") ? `/catalogo${location.search}` : to === "/producao" && location.pathname.startsWith("/producao/") ? `/producao${location.search}` : to}
                          end={end}
                          onClick={closeMobileNavigation} />}>
                          <Icon />
                          <span>{label}</span>
                        </SidebarMenuButton>
                    )}
                    {to === "/minhas-tarefas" && readyTaskCount > 0 ? <SidebarMenuBadge className="right-2.5 w-5 px-0 peer-data-[size=default]/menu-button:top-2" aria-label={`${readyTaskCount} ${readyTaskCount === 1 ? "tarefa pronta" : "tarefas prontas"}`}><Badge variant="secondary" className="min-w-5 px-1 tabular-nums">{readyTaskCount}</Badge></SidebarMenuBadge> : null}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </nav>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto px-3 pb-4 transition-[padding] duration-200 ease-linear group-data-[collapsible=icon]:px-4">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              <Collapsible open={settingsOpen} onOpenChange={changeSettingsOpen} render={<SidebarMenuItem />}>
                  <CollapsibleTrigger render={<SidebarMenuButton type="button" aria-label="Configurações" isActive={inSettings} tooltip="Configurações" className="h-9 px-3 font-sans font-medium" />}>
                      <Settings />
                      <span>Configurações</span>
                      {settingsOpen ? <ChevronsDownUp className="ml-auto" /> : <ChevronsUpDown className="ml-auto" />}
                    </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {settingsNavigation.filter((item) => !item.adminOnly || user?.role === "ADMIN").map(({ to, label, disabled }) => (
                        <SidebarMenuSubItem key={to}>
                          {disabled ? (
                            <SidebarMenuSubButton aria-disabled="true" className="pointer-events-none opacity-50">
                              <span>{label}</span>
                            </SidebarMenuSubButton>
                          ) : (
                            <SidebarMenuSubButton isActive={isRouteActive(location.pathname, to)} render={<NavLink to={to} onClick={closeMobileNavigation} />}>
                                <span>{label}</span>
                              </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </Collapsible>
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Feedback" className="h-9 px-3 font-sans font-medium" render={<a href={feedbackUrl.href} target="_blank" rel="noopener noreferrer" onClick={closeMobileNavigation} />}>
                    <MessageSquarePlus />
                    <span>Feedback</span>
                  </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ChangelogDialog version={appVersion} className="h-9 px-3 font-sans font-medium" />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="rounded-none border-t p-4">
        {isDemo ? <div className="mb-1 rounded-md border bg-background px-3 py-2 text-xs font-medium text-muted-foreground group-data-[collapsible=icon]:hidden">Modo demonstração</div> : null}
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
            <p className="truncate text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user ? roleLabels[user.role] : null}</p>
          </div>
          <Tooltip>
            <TooltipTrigger render={<SidebarMenuButton type="button" className="size-8 cursor-pointer" onClick={() => void signOut()} aria-label="Sair" />}>
                <LogOut />
                <span className="sr-only">Sair</span>
              </TooltipTrigger>
            <TooltipContent side="top">Sair</TooltipContent>
          </Tooltip>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

function AppShellContent() {
  const [topBarSlot, setTopBarSlot] = useState<HTMLDivElement | null>(null)

  return (
    <>
      <AppSidebar />
      <SidebarInset className="min-w-0">
        <header className="flex h-14 shrink-0 items-center border-b bg-background/95 px-4 backdrop-blur lg:hidden">
          <SidebarTrigger aria-label="Abrir menu" className="active:scale-[0.96] transition-transform" />
        </header>
        <div ref={setTopBarSlot} data-slot="page-top-bar-slot" className="sticky top-0 z-20 h-0" />
        <PageTopBarSlotContext.Provider value={topBarSlot}>
          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6"><Outlet /></div>
        </PageTopBarSlotContext.Provider>
      </SidebarInset>
    </>
  )
}

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === "undefined" || window.localStorage.getItem(sidebarStorageKey) !== "true")

  function changeSidebarOpen(open: boolean) {
    setSidebarOpen(open)
    window.localStorage.setItem(sidebarStorageKey, String(!open))
  }

  return (
    <TooltipProvider delay={300}>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={changeSidebarOpen}
        style={{ "--sidebar-width-icon": "4rem" } as CSSProperties}
      >
        <AppShellContent />
      </SidebarProvider>
    </TooltipProvider>
  )
}
